<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useStore } from 'dashboard/composables/store';
import kraftchatBillingAPI from 'dashboard/api/kraftchatBilling';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const SELF_SERVE_TIER_ORDER = ['free', 'start', 'growth'];
const CHANNEL_KEYS = ['whatsapp', 'facebook', 'email', 'instagram'];

const { t } = useI18n();
const { currentAccount } = useAccount();
const store = useStore();

const plan = computed(() => currentAccount.value?.kraftchat_plan || {});
const planKey = computed(() => plan.value.plan_key);
const usage = computed(() => plan.value.ai_conversations || {});
const isUnlimited = computed(() => usage.value.unlimited);
const percentage = computed(() => usage.value.percentage || 0);
const limitReached = computed(
  () => !isUnlimited.value && percentage.value >= 100
);
const availableChannels = computed(() => plan.value.available_channels || {});
const subscription = computed(() => plan.value.subscription);
const pendingChange = computed(() => subscription.value?.pending_change);
const isCancellationScheduled = computed(
  () => subscription.value?.cancel_at_period_end && !pendingChange.value
);
const hasPendingSubscriptionAction = computed(
  () => Boolean(pendingChange.value) || isCancellationScheduled.value
);

// Administrators only - the backend redacts these fields for everyone else,
// so they are simply absent here for a non-admin viewer.
const isPastDue = computed(
  () => subscription.value?.subscription_status === 'past_due'
);
const paymentRequiresAction = computed(() =>
  Boolean(subscription.value?.payment_requires_action)
);
const paymentFinalizationFailed = computed(() =>
  Boolean(subscription.value?.payment_finalization_failed)
);

const barColorClass = computed(() => {
  if (percentage.value >= 100) return 'bg-n-ruby-9';
  if (percentage.value >= 80) return 'bg-n-amber-9';
  return 'bg-n-brand';
});

// Self-serve upgrade/downgrade only covers Free/Start/Growth - Enterprise is
// a manual/negotiated plan on both ends (see ENTERPRISE_NOTE below).
const changeOptions = computed(() => {
  const currentIndex = SELF_SERVE_TIER_ORDER.indexOf(planKey.value);
  if (currentIndex === -1) return [];

  return SELF_SERVE_TIER_ORDER.filter(tier => tier !== 'free').filter(
    tier => tier !== planKey.value
  );
});

const isUpgrade = targetPlanKey =>
  SELF_SERVE_TIER_ORDER.indexOf(targetPlanKey) >
  SELF_SERVE_TIER_ORDER.indexOf(planKey.value);

const isLoadingAction = ref(false);
const actionError = ref('');
const confirmDialog = ref(null);
const pendingDowngradeTarget = ref(null);

const planLabel = key =>
  t(`KRAFTCHAT_BILLING.CURRENT_PLAN.${key.toUpperCase()}`);

const formatDate = isoOrDate => {
  if (!isoOrDate) return '';
  return new Date(isoOrDate).toLocaleDateString();
};

const changePlan = async targetPlanKey => {
  isLoadingAction.value = true;
  actionError.value = '';
  try {
    if (subscription.value) {
      await kraftchatBillingAPI.changeSubscription({
        planKey: targetPlanKey,
        billingCycle: 'monthly',
      });
      await store.dispatch('accounts/get');
    } else {
      const response = await kraftchatBillingAPI.createCheckoutSession({
        planKey: targetPlanKey,
        billingCycle: 'monthly',
      });
      window.location = response.data.checkout_url;
    }
  } catch (error) {
    actionError.value = t('KRAFTCHAT_BILLING.UPGRADE.ERROR');
  } finally {
    isLoadingAction.value = false;
  }
};

const handlePlanButtonClick = targetPlanKey => {
  if (isUpgrade(targetPlanKey)) {
    changePlan(targetPlanKey);
    return;
  }
  pendingDowngradeTarget.value = targetPlanKey;
  confirmDialog.value?.open();
};

const confirmDowngrade = () => {
  changePlan(pendingDowngradeTarget.value);
  confirmDialog.value?.close();
};

const cancelDialog = ref(null);

const cancelSubscription = async () => {
  isLoadingAction.value = true;
  actionError.value = '';
  try {
    await kraftchatBillingAPI.cancelSubscription();
    await store.dispatch('accounts/get');
    cancelDialog.value?.close();
  } catch (error) {
    actionError.value = t('KRAFTCHAT_BILLING.UPGRADE.ERROR');
  } finally {
    isLoadingAction.value = false;
  }
};

const undoCancellation = async () => {
  isLoadingAction.value = true;
  actionError.value = '';
  try {
    await kraftchatBillingAPI.undoCancellation();
    await store.dispatch('accounts/get');
  } catch (error) {
    actionError.value = t('KRAFTCHAT_BILLING.UPGRADE.ERROR');
  } finally {
    isLoadingAction.value = false;
  }
};
</script>

<template>
  <div class="flex flex-col gap-4 w-full py-4">
    <h1 class="text-xl font-medium text-n-slate-12">
      {{ t('KRAFTCHAT_BILLING.TITLE') }}
    </h1>

    <section class="p-4 rounded-xl border border-n-weak">
      <span class="text-sm text-n-slate-11">
        {{ t('KRAFTCHAT_BILLING.CURRENT_PLAN.TITLE') }}
      </span>
      <p class="text-lg font-medium text-n-slate-12">
        {{ planLabel(planKey) }}
      </p>
    </section>

    <section
      v-if="pendingChange"
      class="p-4 rounded-xl border border-n-amber-6 bg-n-amber-2"
    >
      <p class="text-sm text-n-amber-11">
        {{
          t('KRAFTCHAT_BILLING.PENDING_CHANGE.DOWNGRADE', {
            plan: planLabel(pendingChange.plan_key),
            date: formatDate(pendingChange.effective_at),
          })
        }}
      </p>
    </section>

    <section
      v-else-if="isCancellationScheduled"
      class="p-4 rounded-xl border border-n-amber-6 bg-n-amber-2 flex items-center justify-between gap-3"
    >
      <p class="text-sm text-n-amber-11">
        {{
          t('KRAFTCHAT_BILLING.PENDING_CHANGE.CANCELLATION', {
            date: formatDate(subscription.current_period_end),
          })
        }}
      </p>
      <NextButton
        :label="t('KRAFTCHAT_BILLING.PENDING_CHANGE.UNDO')"
        sm
        ghost
        :is-loading="isLoadingAction"
        :disabled="isLoadingAction"
        @click="undoCancellation"
      />
    </section>

    <section
      v-if="paymentFinalizationFailed"
      class="p-4 rounded-xl border border-n-ruby-6 bg-n-ruby-2"
    >
      <p class="text-sm text-n-ruby-11">
        {{ t('KRAFTCHAT_BILLING.PAYMENT_ISSUE.FINALIZATION_FAILED') }}
      </p>
    </section>
    <section
      v-else-if="paymentRequiresAction"
      class="p-4 rounded-xl border border-n-amber-6 bg-n-amber-2"
    >
      <p class="text-sm text-n-amber-11">
        {{ t('KRAFTCHAT_BILLING.PAYMENT_ISSUE.ACTION_REQUIRED') }}
      </p>
    </section>
    <section
      v-else-if="isPastDue"
      class="p-4 rounded-xl border border-n-amber-6 bg-n-amber-2"
    >
      <p class="text-sm text-n-amber-11">
        {{ t('KRAFTCHAT_BILLING.PAYMENT_ISSUE.PAST_DUE') }}
      </p>
    </section>

    <section class="p-4 rounded-xl border border-n-weak">
      <span class="text-sm text-n-slate-11">
        {{ t('KRAFTCHAT_BILLING.AI_USAGE.TITLE') }}
      </span>
      <div v-if="isUnlimited" class="mt-2 text-n-slate-12">
        {{ t('KRAFTCHAT_BILLING.AI_USAGE.UNLIMITED') }}
      </div>
      <div v-else class="mt-2 flex flex-col gap-2">
        <div class="flex justify-between text-sm text-n-slate-11">
          <span>{{ usage.used }} / {{ usage.limit }}</span>
          <span>{{ percentage }}%</span>
        </div>
        <div class="rounded-full overflow-hidden h-2 w-full bg-n-slate-4">
          <div
            class="h-2 transition-all"
            :class="barColorClass"
            :style="{ width: `${Math.min(percentage, 100)}%` }"
          />
        </div>
        <p v-if="limitReached" class="text-sm text-n-ruby-11">
          {{ t('KRAFTCHAT_BILLING.AI_USAGE.LIMIT_REACHED') }}
        </p>
      </div>
    </section>

    <section class="p-4 rounded-xl border border-n-weak">
      <span class="text-sm text-n-slate-11">
        {{ t('KRAFTCHAT_BILLING.CHANNELS.TITLE') }}
      </span>
      <ul class="mt-2 flex flex-col gap-1">
        <li
          v-for="channel in CHANNEL_KEYS"
          :key="channel"
          class="flex items-center gap-2 text-sm"
        >
          <span
            :class="
              availableChannels[channel] ? 'text-n-teal-11' : 'text-n-slate-9'
            "
          >
            {{ availableChannels[channel] ? '✓' : '—' }}
          </span>
          <span class="text-n-slate-12">
            {{ t(`KRAFTCHAT_BILLING.CHANNELS.${channel.toUpperCase()}`) }}
          </span>
        </li>
      </ul>
    </section>

    <section class="p-4 rounded-xl border border-n-weak">
      <span class="text-sm text-n-slate-11">
        {{ t('KRAFTCHAT_BILLING.VOICE_AI.TITLE') }}
      </span>
      <p class="mt-1 text-sm text-n-slate-12">
        {{
          plan.voice_ai_enabled
            ? t('KRAFTCHAT_BILLING.VOICE_AI.ENABLED')
            : t('KRAFTCHAT_BILLING.VOICE_AI.DISABLED')
        }}
      </p>
    </section>

    <section
      v-if="changeOptions.length && !hasPendingSubscriptionAction"
      class="p-4 rounded-xl border border-n-weak flex flex-col gap-3"
    >
      <span class="text-sm text-n-slate-11">
        {{ t('KRAFTCHAT_BILLING.UPGRADE.TITLE') }}
      </span>
      <div class="flex flex-wrap gap-2">
        <NextButton
          v-for="option in changeOptions"
          :key="option"
          :label="
            isUpgrade(option)
              ? t('KRAFTCHAT_BILLING.UPGRADE.BUTTON', {
                  plan: planLabel(option),
                })
              : t('KRAFTCHAT_BILLING.DOWNGRADE.BUTTON', {
                  plan: planLabel(option),
                })
          "
          :variant="isUpgrade(option) ? 'solid' : 'faded'"
          :is-loading="isLoadingAction"
          :disabled="isLoadingAction"
          @click="handlePlanButtonClick(option)"
        />
      </div>
      <p v-if="actionError" class="text-sm text-n-ruby-11">
        {{ actionError }}
      </p>
    </section>

    <p v-if="planKey !== 'enterprise'" class="text-sm text-n-slate-11">
      {{ t('KRAFTCHAT_BILLING.ENTERPRISE_NOTE') }}
    </p>

    <section
      v-if="subscription && !hasPendingSubscriptionAction"
      class="p-4 rounded-xl border border-n-weak flex items-center justify-between gap-3"
    >
      <div>
        <span class="text-sm text-n-slate-11">
          {{ t('KRAFTCHAT_BILLING.MANAGE_SUBSCRIPTION.TITLE') }}
        </span>
        <p class="mt-1 text-sm text-n-slate-12">
          {{ t('KRAFTCHAT_BILLING.MANAGE_SUBSCRIPTION.UNAVAILABLE') }}
        </p>
      </div>
      <NextButton
        :label="t('KRAFTCHAT_BILLING.CANCEL.BUTTON')"
        sm
        ghost
        ruby
        @click="cancelDialog?.open()"
      />
    </section>
    <section
      v-else-if="!subscription"
      class="p-4 rounded-xl border border-n-weak"
    >
      <span class="text-sm text-n-slate-11">
        {{ t('KRAFTCHAT_BILLING.MANAGE_SUBSCRIPTION.TITLE') }}
      </span>
      <p class="mt-1 text-sm text-n-slate-12">
        {{ t('KRAFTCHAT_BILLING.MANAGE_SUBSCRIPTION.UNAVAILABLE') }}
      </p>
    </section>

    <Dialog
      ref="confirmDialog"
      type="alert"
      :title="
        t('KRAFTCHAT_BILLING.DOWNGRADE.CONFIRM_TITLE', {
          plan: planLabel(pendingDowngradeTarget || ''),
        })
      "
      :description="t('KRAFTCHAT_BILLING.DOWNGRADE.CONFIRM_DESCRIPTION')"
      :confirm-button-label="t('KRAFTCHAT_BILLING.DOWNGRADE.CONFIRM_BUTTON')"
      :is-loading="isLoadingAction"
      @confirm="confirmDowngrade"
    />

    <Dialog
      ref="cancelDialog"
      type="alert"
      :title="t('KRAFTCHAT_BILLING.CANCEL.CONFIRM_TITLE')"
      :confirm-button-label="t('KRAFTCHAT_BILLING.CANCEL.CONFIRM_BUTTON')"
      :is-loading="isLoadingAction"
      @confirm="cancelSubscription"
    >
      <template #description>
        <p class="text-n-slate-11">
          {{ t('KRAFTCHAT_BILLING.CANCEL.CONFIRM_DESCRIPTION') }}
        </p>
        <ul class="mt-2 list-disc list-inside text-n-slate-11">
          <li>{{ t('KRAFTCHAT_BILLING.CANCEL.CONSEQUENCE_INSTAGRAM') }}</li>
          <li>{{ t('KRAFTCHAT_BILLING.CANCEL.CONSEQUENCE_CHANNELS') }}</li>
          <li>{{ t('KRAFTCHAT_BILLING.CANCEL.CONSEQUENCE_VOICE') }}</li>
          <li>{{ t('KRAFTCHAT_BILLING.CANCEL.CONSEQUENCE_AI_LIMIT') }}</li>
          <li>{{ t('KRAFTCHAT_BILLING.CANCEL.CONSEQUENCE_DATA_KEPT') }}</li>
        </ul>
      </template>
    </Dialog>
  </div>
</template>
